import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FilePlus2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { ConfirmDelete } from "@/components/common/ConfirmDelete";
import { CLAIM_STATUS, StatusChip, Thread } from "@/components/common/Thread";
import { useNetwork } from "@/components/household/RepairsPanel";
import { GuaranteesSection } from "@/components/household/GuaranteesEditor";
import { InsuranceReview } from "@/components/household/InsuranceReview";
import { ClaimEmailDialog } from "@/components/household/ClaimEmail";
import { OrgLogo } from "@/lib/banks";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/common/DatePicker";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useHousehold } from "@/context/HouseholdContext";
import { useAssets } from "@/hooks/use-assets";
import { useHouseholdDetail } from "@/hooks/use-household-detail";
import { api, formatApiError } from "@/lib/api";
import { useFormat } from "@/lib/format";

const KINDS = { habitation: "Habitation", multirisque: "Multirisque habitation", appareils: "Appareils nomades", auto: "Automobile", autre: "Autre" };
const today = () => new Date().toISOString().slice(0, 10);

export const useInsurance = () => {
  const { active } = useHousehold();
  return useQuery({ queryKey: ["insurance", active?.id], queryFn: () => api.get(`/households/${active.id}/insurance`).then((r) => r.data), enabled: !!active });
};

const AssetChecklist = ({ value, onChange }) => {
  const { data: assets = [] } = useAssets();
  return (
    <div className="max-h-48 space-y-1.5 overflow-y-auto rounded-xl border border-border p-3" data-testid="coverage-checklist">
      {assets.map((a) => (
        <label key={a.id} className="flex items-center gap-2 text-sm">
          <Checkbox checked={value.includes(a.id)} onCheckedChange={(c) => onChange(c ? [...value, a.id] : value.filter((x) => x !== a.id))} data-testid={`coverage-asset-${a.id}`} />
          {a.name}<span className="text-xs text-muted-foreground">{a.room}</span>
        </label>
      ))}
    </div>
  );
};

const ContractDialog = ({ onOpenChange }) => {
  const { active } = useHousehold();
  const qc = useQueryClient();
  const { data: insurers = [] } = useNetwork("insurer");
  const [v, setV] = useState({ org_id: "", kind: "habitation", number: "", start: today(), end: "", premium_month: "", asset_ids: [] });
  const set = (k) => (e) => setV({ ...v, [k]: e?.target ? e.target.value : e });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/households/${active.id}/insurance`, { ...v, end: v.end || null, premium_month: v.premium_month === "" ? null : Number(v.premium_month) });
      qc.invalidateQueries({ queryKey: ["insurance"] });
      toast.success("Contrat ajouté");
      onOpenChange(false);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="contract-dialog">
        <DialogHeader><DialogTitle className="font-heading">Nouveau contrat d'assurance</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <Select value={v.org_id} onValueChange={set("org_id")}><SelectTrigger data-testid="contract-insurer-select"><SelectValue placeholder="Assureur" /></SelectTrigger>
            <SelectContent>{insurers.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent></Select>
          <div className="grid grid-cols-2 gap-3">
            <Select value={v.kind} onValueChange={set("kind")}><SelectTrigger data-testid="contract-kind-select"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(KINDS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select>
            <Input value={v.number} onChange={set("number")} placeholder="N° de contrat" required minLength={3} data-testid="contract-number-input" />
            <div><Label className="text-xs">Début</Label><DatePicker value={v.start} onChange={set("start")} required placeholder="Début du contrat" data-testid="contract-start-input" /></div>
            <div><Label className="text-xs">Fin</Label><DatePicker value={v.end} onChange={set("end")} placeholder="Fin du contrat" data-testid="contract-end-input" /></div>
          </div>
          <Input type="number" min="0" step="0.01" value={v.premium_month} onChange={set("premium_month")} placeholder="Cotisation mensuelle €" data-testid="contract-premium-input" />
          <Label>Équipements couverts</Label>
          <AssetChecklist value={v.asset_ids} onChange={(asset_ids) => setV({ ...v, asset_ids })} />
          <Button type="submit" className="w-full rounded-full" disabled={!v.org_id} data-testid="contract-submit-button">Enregistrer</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const ClaimDialog = ({ contract, onOpenChange, assetId, onCreated }) => {
  const { active } = useHousehold();
  const qc = useQueryClient();
  const { data: assets = [] } = useAssets();
  const [v, setV] = useState({ asset_id: assetId || "none", incident_date: today(), description: "", amount_claimed: "" });
  const set = (k) => (e) => setV({ ...v, [k]: e?.target ? e.target.value : e });
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post(`/households/${active.id}/insurance/${contract.id}/claims`, { ...v, asset_id: v.asset_id === "none" ? null : v.asset_id, amount_claimed: v.amount_claimed === "" ? null : Number(v.amount_claimed) });
      qc.invalidateQueries({ queryKey: ["insurance"] });
      toast.success("Sinistre créé : votre email de déclaration est prêt");
      onOpenChange(false);
      onCreated?.(data);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent data-testid="claim-dialog">
        <DialogHeader><DialogTitle className="font-heading">Déclarer un sinistre · {contract.number}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <Select value={v.asset_id} onValueChange={set("asset_id")}><SelectTrigger data-testid="claim-asset-select"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="none">Aucun équipement précis</SelectItem>{assets.filter((a) => contract.asset_ids.includes(a.id)).map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent></Select>
          <div className="grid grid-cols-2 gap-3">
            <DatePicker value={v.incident_date} onChange={set("incident_date")} required noFuture placeholder="Date du sinistre" data-testid="claim-date-input" />
            <Input type="number" min="0" step="0.01" value={v.amount_claimed} onChange={set("amount_claimed")} placeholder="Montant estimé €" data-testid="claim-amount-input" />
          </div>
          <Textarea value={v.description} onChange={set("description")} required minLength={5} placeholder="Que s'est-il passé ?" data-testid="claim-description-input" />
          <Button type="submit" className="w-full rounded-full" data-testid="claim-submit-button">Déclarer</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const ContractCard = ({ c, canManage, canEdit }) => {
  const { active } = useHousehold();
  const qc = useQueryClient();
  const { money } = useFormat();
  const { data: assets = [] } = useAssets();
  const [edit, setEdit] = useState(false);
  const [cov, setCov] = useState(c.asset_ids);
  const [claim, setClaim] = useState(false);
  const [mail, setMail] = useState(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ["insurance"] });
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); refresh(); } catch (e) { toast.error(formatApiError(e)); } };
  return (
    <li className="card-surface space-y-4 p-5" data-testid={`contract-${c.id}`}>
      <div className="flex flex-wrap items-center gap-3">
        <OrgLogo name={c.org_name} className="h-10 w-10" />
        <div className="min-w-0 flex-1 basis-56"><div className="font-semibold">{KINDS[c.kind]} · {c.org_name}</div>
          <div className="text-xs text-muted-foreground">N° {c.number} · {new Date(c.start).toLocaleDateString("fr-FR")}{c.end && ` → ${new Date(c.end).toLocaleDateString("fr-FR")}`}{c.premium_month != null && ` · ${money(c.premium_month, { maximumFractionDigits: 2 })}/mois`}</div></div>
        {canEdit && <Button size="sm" variant="outline" className="rounded-full" onClick={() => setClaim(true)} data-testid={`claim-new-${c.id}`}>Déclarer un sinistre</Button>}
        {canManage && <ConfirmDelete title="Supprimer ce contrat ?" text={`Le contrat ${c.number} sera retiré de votre foyer.`} testid={`contract-delete-${c.id}`} onConfirm={() => act(() => api.delete(`/households/${active.id}/insurance/${c.id}`), "Contrat supprimé")} />}
      </div>
      <GuaranteesSection c={c} canManage={canManage} />
      <div>
        <div className="mb-1.5 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Équipements couverts ({c.asset_ids.length})
          {canManage && <button className="normal-case text-primary underline" onClick={() => setEdit(!edit)} data-testid={`coverage-edit-${c.id}`}>{edit ? "Fermer" : "Modifier"}</button>}</div>
        {edit ? <div className="space-y-2"><AssetChecklist value={cov} onChange={setCov} /><Button size="sm" className="rounded-full" onClick={() => act(() => api.patch(`/households/${active.id}/insurance/${c.id}`, { asset_ids: cov }).then(() => setEdit(false)), "Couverture mise à jour")} data-testid={`coverage-save-${c.id}`}>Enregistrer</Button></div>
          : <div className="flex flex-wrap gap-1.5">{c.asset_ids.map((id) => <span key={id} className="tone-chip tone-sky rounded-full px-2.5 py-0.5 text-xs">{assets.find((a) => a.id === id)?.name || "Équipement"}</span>)}</div>}
      </div>
      {c.claims.length > 0 && (
        <ul className="space-y-3" data-testid={`claims-${c.id}`}>
          {c.claims.map((x) => (
            <li key={x.id} className="rounded-xl border border-border p-3 text-sm" data-testid={`claim-${x.id}`}>
              <div className="flex flex-wrap items-center gap-2"><span className="font-mono text-xs text-muted-foreground">{x.ref}</span><span className="flex-1 font-semibold">{x.asset_name || "Sinistre"}</span><StatusChip map={CLAIM_STATUS} status={x.status} testid={`claim-status-${x.id}`} /></div>
              <p className="my-2 text-muted-foreground">{x.description}</p>
              <div className="mb-2 flex flex-wrap items-center gap-2 text-xs"><span>Déclaré : {x.amount_claimed != null ? money(x.amount_claimed) : "—"}{x.amount_approved != null && <> · Accordé : <b>{money(x.amount_approved)}</b></>}</span>
                <span className="text-muted-foreground">· {(x.attachments || []).length} PJ · {(x.emails || []).length ? `email envoyé ${(x.emails || []).length}×` : "email non envoyé"}</span>
                {canEdit && <Button size="sm" className="ml-auto h-7 rounded-full" onClick={() => setMail(x)} data-testid={`claim-email-open-${x.id}`}>Email de déclaration</Button>}</div>
              <Thread messages={x.messages} testid={`claim-thread-${x.id}`} onSend={canEdit ? async (text) => { await api.post(`/households/${active.id}/claims/${x.id}/messages`, { text }); refresh(); } : null} />
            </li>
          ))}
        </ul>
      )}
      {claim && <ClaimDialog contract={c} onOpenChange={setClaim} onCreated={setMail} />}
      {mail && <ClaimEmailDialog hid={active.id} claim={mail} onOpenChange={() => setMail(null)} />}
    </li>
  );
};

export const InsurancePanel = () => {
  const { canManage, canEdit } = useHouseholdDetail();
  const { data = [] } = useInsurance();
  const [open, setOpen] = useState(false);
  return (
    <section className="space-y-5" data-testid="insurance-panel">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="flex-1 text-lg font-bold">Contrats d'assurance</h2>
        {canManage && <Button className="rounded-full" onClick={() => setOpen(true)} data-testid="contract-new-button"><FilePlus2 className="mr-2 h-4 w-4" />Ajouter un contrat</Button>}
      </div>
      <InsuranceReview canManage={canManage} />
      {data.length ? <ul className="space-y-4">{data.map((c) => <ContractCard key={c.id} c={c} canManage={canManage} canEdit={canEdit} />)}</ul>
        : <p className="text-sm text-muted-foreground">Aucun contrat enregistré.</p>}
      <p className="text-xs text-muted-foreground">L'assureur ne voit que les contrats et sinistres que vous lui déclarez, jamais le reste de votre foyer.</p>
      {open && <ContractDialog onOpenChange={setOpen} />}
    </section>
  );
};
